import { useState, useMemo } from "react";
import { FaPlus, FaSearch, FaEnvelope } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  Container,
  PageHeader,
  ContentSection,
  FAQContainer,
  FAQItem,
  FAQQuestion,
  FAQAnswer,
  CategorySection,
  CategoryTitle,
  NoResults,
  ContactCTA,
} from "./FAQPage.styles";
import MainContainer from "../../components/MainContainer/MainContainer";
import SEO from "../../components/SEO/SEO";

const FAQPage = () => {
  const navigate = useNavigate();
  const activeCategory = "sve";
  const [openItems, setOpenItems] = useState(new Set());

  const faqData = [
    {
      id: 1,
      category: "kursevi",
      question: "Kome je namenjena edukacija?",
      answer:
        "Edukacija je namenjena svima, početnicima koji tek žele da nauče kako da rade tretmane Lash i Brow lift i već iskusnim Lash & Brow Artistima! Nije potrebno predznanje.",
    },
    {
      id: 2,
      category: "kursevi",
      question: "Kako odabrati adekvatan materijal za rad?",
      answer:
        "Tokom edukacije sam navela 6 razlicitih brendova u različitim cenovnim rangovima koji mogu da se koriste. Takodje, u skripti imate detaljan spisak proizvoda koji ti je potreban za samostalan rad.",
    },
    {
      id: 3,
      category: "sertifikat",
      question: "Kako se dobija sertifikat?",
      answer:
        "Sertifikat o završenoj Lash i/ili Brow Lift Bible online edukaciji se dobija tek kada se odradi zadatak. Potrebno je da pošalješ svoj rad nakon edukacije (slike pre, posle i tokom rada). Ukoliko je rad zadovoljavajući, na mejl ti stiže sertifikat o završenoj edukaciji.",
    },
    {
      id: 4,
      category: "tehnicko",
      question: "Koliko dugo imam pristup?",
      answer:
        "Pristup online edukaciji imaš 6 meseci od momenta kupovine. U tih 6 meseci imaš potpuno pravo da gledaš edukaciju, video sadržaj koliko god puta hoćeš. Nakon 6 meseci videi će se zaključati.",
    },
    {
      id: 5,
      category: "kursevi",
      question: "Da li dobijem skriptu?",
      answer:
        "Uz svaku kupljenu edukaciju se dobija PDF skripta (40+ strana) koja direktno prati edukaciju u kojoj je svaki korak detaljno zapisan i objašnjen.",
    },
    {
      id: 6,
      category: "kursevi",
      question: "Da li moram da koristim isti materijal kao i ti?",
      answer:
        "Tokom edukacije naučićeš da radiš Lash i/ili Brow lift na svim tipovima klijenata sa bilo kojim materijalom, tako da, imaš potpunu slobodu da izabereš materijal koji hoceš da koristiš.",
    },
  ];

  const categories = [
    { id: "sve", name: "Sve Kategorije" },
    { id: "kursevi", name: "Kursevi" },
    { id: "tehnicko", name: "Tehnička Podrška" },
    { id: "sertifikati", name: "Sertifikati" },
    { id: "podrska", name: "Podrška" },
  ];

  const filteredFAQs = useMemo(() => {
    let filtered = faqData;

    // Filter by category
    if (activeCategory !== "sve") {
      filtered = filtered.filter((faq) => faq.category === activeCategory);
    }

    return filtered;
    // eslint-disable-next-line
  }, [activeCategory]);

  const toggleFAQItem = (id) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id);
    } else {
      newOpenItems.add(id);
    }
    setOpenItems(newOpenItems);
  };

  const groupedFAQs = useMemo(() => {
    const grouped = {};
    filteredFAQs.forEach((faq) => {
      if (!grouped[faq.category]) {
        grouped[faq.category] = [];
      }
      grouped[faq.category].push(faq);
    });
    return grouped;
  }, [filteredFAQs]);

  const onNavigate = (path) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  return (
    <MainContainer>
      <SEO
        title="Česta pitanja"
        description="Odgovori na najčešća pitanja o online edukacijama: kome su namenjene, materijali, sertifikat, trajanje pristupa i PDF skripta."
        path="/cesta-pitanja"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqData.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: { "@type": "Answer", text: faq.answer },
          })),
        }}
      />

      <Container>
        <PageHeader>
          <h1>Često Postavljena Pitanja</h1>
          <p>
            Pronađite odgovore na najčešća pitanja o našim kursevima, tehničkoj
            podršci i više.
          </p>
        </PageHeader>

        <ContentSection>
          {/* Category Tabs */}
          {/*        <CategoryTabs>
            {categories.map((category) => (
              <CategoryTab
                key={category.id}
                $active={activeCategory === category.id}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </CategoryTab>
            ))}
          </CategoryTabs>*/}

          {/* FAQ Content */}
          {filteredFAQs.length === 0 ? (
            <NoResults>
              <FaSearch className="icon" />
              <h3>Nema rezultata</h3>
              <p>
                Nismo pronašli pitanja koja odgovaraju vašoj pretrazи.
                <br />
                Pokušajte sa drugačijim ključnim rečima ili kontaktirajte našu
                podršku.
              </p>
            </NoResults>
          ) : activeCategory === "sve" ? (
            // Show grouped by category when showing all
            Object.entries(groupedFAQs).map(([categoryId, faqs]) => {
              const categoryName =
                categories.find((cat) => cat.id === categoryId)?.name ||
                categoryId;
              return (
                <CategorySection key={categoryId}>
                  <CategoryTitle>{categoryName}</CategoryTitle>
                  <FAQContainer>
                    {faqs.map((faq) => (
                      <FAQItem key={faq.id}>
                        <FAQQuestion
                          $isOpen={openItems.has(faq.id)}
                          onClick={() => toggleFAQItem(faq.id)}
                        >
                          <h3>{faq.question}</h3>
                          <FaPlus className="icon" />
                        </FAQQuestion>
                        <FAQAnswer $isOpen={openItems.has(faq.id)}>
                          <div className="answer-content">
                            <p>{faq.answer}</p>
                          </div>
                        </FAQAnswer>
                      </FAQItem>
                    ))}
                  </FAQContainer>
                </CategorySection>
              );
            })
          ) : (
            // Show single category
            <FAQContainer>
              {filteredFAQs.map((faq) => (
                <FAQItem key={faq.id}>
                  <FAQQuestion
                    $isOpen={openItems.has(faq.id)}
                    onClick={() => toggleFAQItem(faq.id)}
                  >
                    <h3>{faq.question}</h3>
                    <FaPlus className="icon" />
                  </FAQQuestion>
                  <FAQAnswer $isOpen={openItems.has(faq.id)}>
                    <div className="answer-content">
                      <p>{faq.answer}</p>
                    </div>
                  </FAQAnswer>
                </FAQItem>
              ))}
            </FAQContainer>
          )}

          {/* Contact CTA */}
          <ContactCTA>
            <h3>Niste pronašli odgovor?</h3>
            <p>
              Naš tim je tu da vam pomogne! Kontaktirajte nas direktno i
              odgovoriće vam u najkraćem mogućem roku.
            </p>
            <span
              className="contact-button"
              onClick={(e) => {
                e.preventDefault();
                onNavigate("/kontakt");
              }}
            >
              <FaEnvelope />
              Kontaktirajte Nas
            </span>
          </ContactCTA>
        </ContentSection>
      </Container>
    </MainContainer>
  );
};

export default FAQPage;
